import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import {
  Shield, MessageSquare, Clock, CheckCircle2, AlertTriangle,
  Send, FileDown, ChevronRight, Loader2, RefreshCw, Filter
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_ORDER = { urgente: 0, alta: 1, media: 2, baixa: 3 };
const PRIORITY_CONFIG = {
  urgente: { label: "Urgente", cls: "bg-red-100 text-red-800 border-red-200" },
  alta:    { label: "Alta",    cls: "bg-orange-100 text-orange-800 border-orange-200" },
  media:   { label: "Média",   cls: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  baixa:   { label: "Baixa",   cls: "bg-green-100 text-green-800 border-green-200" },
};
const STATUS_CONFIG = {
  pendente:   { label: "Pendente",   cls: "bg-yellow-100 text-yellow-800" },
  em_analise: { label: "Em Análise", cls: "bg-blue-100 text-blue-800" },
  respondido: { label: "Respondido", cls: "bg-green-100 text-green-800" },
  encerrado:  { label: "Encerrado",  cls: "bg-gray-100 text-gray-500" },
};
const WOUND_LABELS = {
  ulcera_venosa: "Úlcera Venosa",
  pe_diabetico: "Pé Diabético",
  lesao_por_pressao: "Lesão por Pressão",
};

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(h / 24);
  if (d > 0) return `há ${d}d`;
  if (h > 0) return `há ${h}h`;
  return "agora";
}

export default function EspecialistaDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("abertos"); // abertos | encerrado | all
  const [replying, setReplying] = useState(null); // consult_id
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const resp = await axios.get(`${API}/teleconsultations`, { withCredentials: true });
      setConsultations(resp.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const handleQuickReply = async (consultId) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/teleconsultations/${consultId}/messages`,
        { content: replyText.trim() },
        { withCredentials: true }
      );
      setReplyText("");
      setReplying(null);
      await fetchData();
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  // Filter and sort
  const displayed = consultations
    .filter(c => {
      if (statusFilter === "abertos") return c.status !== "encerrado";
      if (statusFilter === "encerrado") return c.status === "encerrado";
      return true;
    })
    .sort((a, b) => {
      const pa = PRIORITY_ORDER[a.priority] ?? 99;
      const pb = PRIORITY_ORDER[b.priority] ?? 99;
      if (pa !== pb) return pa - pb;
      return new Date(b.created_at) - new Date(a.created_at);
    });

  const stats = {
    total: consultations.length,
    pendente: consultations.filter(c => c.status === "pendente").length,
    em_analise: consultations.filter(c => c.status === "em_analise").length,
    urgente: consultations.filter(c => c.priority === "urgente" && c.status !== "encerrado").length,
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="especialista-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
                Painel do Especialista
              </h1>
              <p className="text-xs text-muted-foreground">Casos em aberto — ordenados por prioridade</p>
            </div>
          </div>
          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="p-2 rounded-lg border border-border hover:bg-secondary transition-all"
            data-testid="refresh-btn"
            title="Atualizar"
          >
            <RefreshCw className={`w-4 h-4 text-muted-foreground ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-in">
          <div className="bg-white border border-border rounded-xl p-4" data-testid="stat-total">
            <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Total de casos</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4" data-testid="stat-pendente">
            <p className="text-2xl font-bold text-yellow-800">{stats.pendente}</p>
            <p className="text-xs text-yellow-600 mt-0.5">Aguardando resposta</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="stat-em-analise">
            <p className="text-2xl font-bold text-blue-800">{stats.em_analise}</p>
            <p className="text-xs text-blue-600 mt-0.5">Em análise</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="stat-urgente">
            <p className="text-2xl font-bold text-red-700">{stats.urgente}</p>
            <p className="text-xs text-red-500 mt-0.5">Urgentes abertos</p>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex gap-2 animate-fade-in">
          {[
            ["abertos", "Casos Abertos"],
            ["encerrado", "Encerrados"],
            ["all", "Todos"],
          ].map(([val, lbl]) => (
            <button key={val} onClick={() => setStatusFilter(val)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                statusFilter === val
                  ? "bg-primary text-primary-foreground"
                  : "bg-white border border-border text-muted-foreground hover:bg-secondary"
              }`}
              data-testid={`filter-${val}`}
            >
              {lbl}
              {val === "abertos" && stats.pendente > 0 && (
                <span className="ml-1.5 bg-destructive text-white text-[10px] px-1.5 rounded-full">
                  {stats.pendente}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Case list */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-28 rounded-xl bg-white border border-border animate-pulse" />
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <div className="bg-white rounded-xl border border-border p-12 text-center animate-fade-in">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <p className="text-base font-semibold text-foreground" style={{ fontFamily: "Manrope" }}>
              {statusFilter === "abertos" ? "Sem casos abertos no momento" : "Nenhum caso encontrado"}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {statusFilter === "abertos" ? "Novos casos aparecerão aqui quando profissionais solicitarem interconsulta." : ""}
            </p>
          </div>
        ) : (
          <div className="space-y-3 animate-fade-in">
            {displayed.map(c => {
              const isReplying = replying === c.consult_id;
              const pCfg = PRIORITY_CONFIG[c.priority];
              const sCfg = STATUS_CONFIG[c.status];
              const isUrgent = c.priority === "urgente" && c.status !== "encerrado";
              return (
                <div
                  key={c.consult_id}
                  className={`bg-white rounded-xl border overflow-hidden transition-all ${
                    isUrgent ? "border-red-300 shadow-sm shadow-red-100" : "border-border"
                  }`}
                  data-testid={`case-card-${c.consult_id}`}
                >
                  <div className="p-4">
                    {/* Top row */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex flex-col gap-1 min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${pCfg?.cls}`}>
                            {pCfg?.label}
                          </span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${sCfg?.cls}`}>
                            {sCfg?.label}
                          </span>
                          {isUrgent && (
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-sm font-bold text-foreground">{c.patient_name || "Paciente"}</p>
                          {c.wound_info?.wound_type && (
                            <span className="text-xs text-muted-foreground">
                              · {WOUND_LABELS[c.wound_info.wound_type]}
                            </span>
                          )}
                          {c.wound_info?.location && (
                            <span className="text-xs text-muted-foreground">· {c.wound_info.location}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Clock className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{timeAgo(c.created_at)}</span>
                      </div>
                    </div>

                    {/* Question preview */}
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{c.question}</p>

                    {/* Requesting professional */}
                    <p className="text-xs text-muted-foreground mb-3">
                      Solicitado por <span className="font-medium text-foreground">{c.requesting_user_name}</span>
                      {c.municipality && ` · ${c.municipality}`}
                    </p>

                    {/* Actions */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => navigate("/teleconsultation")}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-all"
                        data-testid={`open-thread-btn-${c.consult_id}`}
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        Ver Thread
                      </button>

                      {c.wound_info?.wound_id && (
                        <button
                          onClick={() => navigate(`/relatorio/${c.wound_info.wound_id}`)}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-border bg-white rounded-lg text-xs font-medium hover:bg-secondary transition-all"
                          data-testid={`view-report-btn-${c.consult_id}`}
                        >
                          <FileDown className="w-3.5 h-3.5" />
                          Relatório
                        </button>
                      )}

                      {c.status !== "encerrado" && (
                        <button
                          onClick={() => {
                            setReplying(isReplying ? null : c.consult_id);
                            setReplyText("");
                          }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition-all ${
                            isReplying
                              ? "border-primary/30 bg-primary/5 text-primary"
                              : "border-border text-muted-foreground hover:bg-secondary"
                          }`}
                          data-testid={`quick-reply-toggle-${c.consult_id}`}
                        >
                          <Send className="w-3.5 h-3.5" />
                          Resposta Rápida
                        </button>
                      )}
                    </div>

                    {/* Quick reply form */}
                    {isReplying && (
                      <div className="mt-3 border-t border-border pt-3 animate-fade-in" data-testid="quick-reply-form">
                        <textarea
                          value={replyText}
                          onChange={e => setReplyText(e.target.value)}
                          placeholder="Escreva seu parecer especializado..."
                          rows={3}
                          autoFocus
                          className="w-full border border-border rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid="quick-reply-textarea"
                          onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleQuickReply(c.consult_id); }
                          }}
                        />
                        <div className="flex justify-end gap-2 mt-2">
                          <button
                            onClick={() => { setReplying(null); setReplyText(""); }}
                            className="px-3 py-1.5 border border-border rounded-lg text-xs font-medium hover:bg-secondary transition-all"
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={() => handleQuickReply(c.consult_id)}
                            disabled={!replyText.trim() || sending}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:bg-primary/90 transition-all disabled:opacity-60"
                            data-testid="send-quick-reply-btn"
                          >
                            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                            Enviar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
